/**
 * Telling a page that it is running yesterday's code.
 *
 * A single-page app keeps whatever bundle it loaded, so a tab left open across a
 * deploy goes on behaving like the release it started with — which is how an
 * RFQ upload can still reach an endpoint the app no longer uses. The build's
 * asset manifest is the honest answer to "what is deployed right now": it names
 * the current entry bundle, and the page can compare that against the bundle it
 * is actually running.
 */

/** Where the build stamps its file names; written by the production build. */
const MANIFEST_PATH = `${process.env.PUBLIC_URL || ""}/asset-manifest.json`;

const MAIN_SCRIPT_PATTERN = /\/static\/js\/main\.[^/]+\.js$/;

/** Compare paths, not URLs: the manifest is root-relative, a script tag is not. */
const pathOf = (source: string): string => {
  try {
    return new URL(source, window.location.origin).pathname;
  } catch {
    return source;
  }
};

/** The entry bundle the deployed build says it uses. */
export const manifestMainScript = (manifest: unknown): string | null => {
  const record = manifest as {
    files?: Record<string, unknown>;
    entrypoints?: unknown[];
  } | null;
  const named = record?.files?.["main.js"];
  if (typeof named === "string" && named) return pathOf(named);
  const entry = (record?.entrypoints || []).find(
    (value) =>
      typeof value === "string" && MAIN_SCRIPT_PATTERN.test(`/${value}`),
  );
  return typeof entry === "string" ? pathOf(`/${entry}`) : null;
};

/** The entry bundle this page is running, read off its own script tags. */
export const runningMainScript = (): string | null => {
  const scripts = Array.from(document.querySelectorAll("script[src]"));
  for (const script of scripts) {
    const source = script.getAttribute("src") || "";
    const path = pathOf(source);
    if (MAIN_SCRIPT_PATTERN.test(path)) return path;
  }
  return null;
};

/**
 * True only when both are known and they differ. Anything unknown means we
 * cannot tell, and a reload prompt we cannot justify is worse than none: in
 * development there is no hashed bundle to compare at all.
 */
export const isRunningStaleBuild = (
  running: string | null,
  deployed: string | null,
): boolean => !!running && !!deployed && running !== deployed;

/**
 * Whether a newer build is live. Silent about failure — an unreachable manifest
 * or an HTML error page served in its place is not news the buyer needs.
 */
export const checkForNewerBuild = async (): Promise<boolean> => {
  try {
    const response = await fetch(MANIFEST_PATH, { cache: "no-store" });
    if (!response.ok) return false;
    const manifest = await response.json();
    return isRunningStaleBuild(
      runningMainScript(),
      manifestMainScript(manifest),
    );
  } catch {
    return false;
  }
};
